import { ClipboardDocumentIcon, FolderIcon } from '@heroicons/react/24/outline';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { AIAvatarIcon } from '@/features/ai-chat/ui/AIAvatarIcon';
import { AssistantMessageLayout } from '@/features/ai-chat/ui/AssistantMessageLayout';
import { Button, Popover, Text, Tooltip, toast } from '@/shared/ui';

type Props = {
  content: string;
  projectPrompt?: string;
};

/** 事業チャットのAI応答メッセージ。コピー + 参照情報ポップオーバーをアクション行に表示する */
export const AssistantMessage: FC<Props> = ({ content, projectPrompt }) => {
  const { t } = useTranslation();

  return (
    <AssistantMessageLayout
      avatar={<AIAvatarIcon className="size-8 shrink-0" />}
      content={content}
      actions={
        <>
          <Tooltip tooltipContent={t('ai.chat.copy-tooltip')}>
            <Button
              variant="non-outline"
              size="none"
              className="size-6 p-1"
              aria-label={t('ai.chat.copy-button')}
              onClick={() => {
                navigator.clipboard.writeText(content);
                toast.success(t('ai.analysis.copy-success'));
              }}
            >
              <ClipboardDocumentIcon className="size-4 text-text-muted" />
            </Button>
          </Tooltip>
          {projectPrompt && (
            <Popover
              contentAlign="start"
              contentClassName="w-[360px] p-0"
              content={
                <div className="flex flex-col">
                  <div className="border-border-base border-b px-3 py-2">
                    <Text variant="description" className="font-medium">
                      {t('ai.chat.reference-info-heading')}
                    </Text>
                  </div>
                  <div className="max-h-[320px] overflow-y-auto px-3 py-2">
                    <Text variant="description" className="whitespace-pre-wrap">
                      {projectPrompt}
                    </Text>
                  </div>
                </div>
              }
            >
              <Button
                variant="ghost"
                size="sm"
                className="h-6 gap-1 bg-stone-100 px-2 hover:bg-stone-200"
              >
                <FolderIcon className="size-3.5 text-text-muted" />
                <Text as="span" variant="description">
                  {t('ai.chat.reference-info')}
                </Text>
              </Button>
            </Popover>
          )}
        </>
      }
    />
  );
};
